import { useActiveRightStackJid } from '@/app/store/navigationSlice';
import {
   AudiocallIcon,
   BellSimpleSlashIcon,
   Call,
   DropdownIcon,
   GroupAvatarIcon,
   PinIcon,
   VideocallIcon,
} from '@/assets/images';
import { Dropdown, ListTile, MessageStatus, UnreadBadge } from '@/components';
import { CALL_TYPE, CALLKIT_CALLSCREENSTATUS } from '@/features/calls/constants';
import { formatRecentChatTime, normalizeTimestamp } from '@/features/timeformatter';
import { Username, UserProfile } from '@/features/users';
import { useUserInfo } from '@/features/users/model';
import PropTypes from 'prop-types';
import React from 'react';
import { useChatItem } from '../../viewmodels';
import './chatItem.scss';
import { formatUserIdToJid } from '@/shared/utils';

/**
 * Pure rendering component — zero business logic.
 * All actions and derived state come from useChatItem().
 */
const ChatItem = ({ item, index, isPrevActive }) => {
   const activeChatJid = useActiveRightStackJid();
   const {
      handleChatClick,
      chatMenuItems,
      lastMessageDisplay,
      isOngoingCall,
      callType,
      callScreenStatus,
      handleEndCall,
      isMyMessage,
   } = useChatItem(item);

   const userInfo = useUserInfo(item.fromUserId);
   const isMuted = userInfo?.isMuted || false;

   const getCallLabel = () => {
      const isConnected = callScreenStatus === CALLKIT_CALLSCREENSTATUS.CONNECTED;
      if (callType === CALL_TYPE.VIDEO) {
         return isConnected ? 'Video call' : 'Ongoing video call...';
      }
      return isConnected ? 'Audio call' : 'Ongoing audio call...';
   };

   const isActive = activeChatJid === formatUserIdToJid(item.fromUserId, item.chatType);
   const day = formatRecentChatTime(new Date(normalizeTimestamp(item.messageTime)));

   const dynamicClasses = [
      item.unreadCount > 0 ? 'unread' : '',
      isOngoingCall ? 'ongoing-call-item' : '',
      isActive ? 'active' : '',
      index === 0 || isPrevActive ? 'no-border-top' : '',
   ]
      .filter(Boolean)
      .join(' ');

   return (
      <ListTile
         as="div"
         className={dynamicClasses}
         avatar={
            <UserProfile
               userId={item.fromUserId}
               className="chat-avatar chat-avatar-fallback ct-flexCenter"
               defaultIcon={item.chatType === 'groupchat' ? <GroupAvatarIcon /> : null}
            />
         }
         title={<Username userId={item.fromUserId} className="chat-name" />}
         subTitle={
            isOngoingCall ? (
               <div className="ct-flex ct-aiCenter ct-gap6">
                  {callType === CALL_TYPE.VIDEO ? (
                     <VideocallIcon className="chat-message-icon" />
                  ) : (
                     <AudiocallIcon className="chat-message-icon" />
                  )}
                  <span className="chat-message-text">{getCallLabel()}</span>
               </div>
            ) : (
               <>
                  {isMyMessage && item.deleteStatus !== 1 && <MessageStatus msgStatus={item.msgStatus} />}
                  {lastMessageDisplay.icon}
                  <span className="chat-message-text">{lastMessageDisplay.label}</span>
               </>
            )
         }
         trailing={
            isOngoingCall ? (
               <button className="ct-flexCenter end-call-btn" onClick={handleEndCall}>
                  <Call />
               </button>
            ) : (
               <>
                  <span className="chat-time">{day}</span>
                  <div className="ct-flex ct-aiCenter">
                     {isMuted && (
                        <button className="chat-action-btn ct-flexCenter ct-cursor" aria-label="Muted">
                           <BellSimpleSlashIcon className="chat-action-icon" />
                        </button>
                     )}
                     {item.isPinned && (
                        <button className="chat-action-btn ct-flexCenter ct-cursor" aria-label="Pinned">
                           <PinIcon className="chat-action-icon" />
                        </button>
                     )}
                     <UnreadBadge count={item.unreadCount} />
                     {chatMenuItems.length > 0 && (
                        <Dropdown
                           icon={<DropdownIcon className="chat-action-icon" />}
                           items={chatMenuItems}
                           withPadding={false}
                           position="left"
                           offsetLeft={67}
                           offsetTop={15}
                           buttonClassName="chat-action-btn chat-dropdown-btn"
                        />
                     )}
                  </div>
               </>
            )
         }
         onClick={handleChatClick}
         showBack={false}
         onBack={() => {}}
         dotStatus={item.unreadCount > 0 && !isOngoingCall ? 'read' : ''}
      />
   );
};

ChatItem.propTypes = {
   item: PropTypes.shape({
      fromUserId: PropTypes.string.isRequired,
      userId: PropTypes.string,
      publisherId: PropTypes.string,
      toUserId: PropTypes.string,
      chatType: PropTypes.string,
      msgId: PropTypes.string,
      msgType: PropTypes.string,
      profileUpdatedStatus: PropTypes.string,
      msgBody: PropTypes.shape({
         message: PropTypes.string,
         messageType: PropTypes.string,
         mentionedUsersIds: PropTypes.arrayOf(PropTypes.string),
      }),
      createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      unreadCount: PropTypes.number,
      isPinned: PropTypes.bool,
      isOnline: PropTypes.bool,
      messageTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      msgStatus: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      deleteStatus: PropTypes.number,
   }).isRequired,
   index: PropTypes.number,
   isPrevActive: PropTypes.bool,
};

export default React.memo(ChatItem);
